import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Loader2, Map as MapIcon, MapPin, Search, X } from "lucide-react";
import { WorkLocation } from "../../types/works";
import { PlaceSuggestion, useGooglePlacesSuggestions } from "./useGooglePlacesSuggestions";
import { mountLocationPicker, PickedLocation } from "./useGoogleMapPicker";

interface LocationFieldProps {
  accent: "amber" | "orange";
  value?: WorkLocation;
  placeholder: string;
  error?: boolean;
  onChange: (location?: WorkLocation) => void;
}

// Tallinn, Estonia — default map center when no location is set yet.
const DEFAULT_CENTER = { lat: 59.437, lng: 24.7536 };

/**
 * Location picker styled after the mockup's colored-border `.object-pill`.
 * Either type to search Google Places, or switch to map mode and drop a
 * pin — both resolve to the place's name/address and lat/lng.
 */
export const LocationField: React.FC<LocationFieldProps> = ({ accent, value, placeholder, error, onChange }) => {
  const { t } = useTranslation("works");
  const [query, setQuery] = useState(value?.name ?? "");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"search" | "map">("search");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [pickedLocation, setPickedLocation] = useState<PickedLocation | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const { ready, searchPlaces, fetchPlaceLocation } = useGooglePlacesSuggestions();

  const accentBorder = accent === "amber" ? "border-l-[#D97706]" : "border-l-[#F97316]";
  const accentIcon = accent === "amber" ? "text-[#D97706]" : "text-[#F97316]";

  useEffect(() => {
    setQuery(value?.name ?? "");
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setMode("search");
        setSuggestions([]);
        setQuery(value?.name ?? "");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, value]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (!open || mode !== "search" || !ready || trimmed.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      searchPlaces(query).then((results) => {
        setSuggestions(results);
        setLoading(false);
      });
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, ready, open, mode, searchPlaces]);

  useEffect(() => {
    if (!open || mode !== "map" || !mapContainerRef.current) return;
    let cancelled = false;
    let handle: { destroy: () => void } | null = null;

    const center =
      value?.latitude != null && value?.longitude != null
        ? { lat: value.latitude, lng: value.longitude }
        : DEFAULT_CENTER;
    const marker = value?.latitude != null && value?.longitude != null ? center : null;

    setPickedLocation(
      value?.latitude != null && value?.longitude != null
        ? { name: value.name, address: value.address ?? "", latitude: value.latitude, longitude: value.longitude }
        : null,
    );

    mountLocationPicker(mapContainerRef.current, center, marker, setPickedLocation, setMapLoading).then((h) => {
      if (cancelled) h.destroy();
      else handle = h;
    });

    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, [open, mode, value]);

  const handleSelectSuggestion = async (suggestion: PlaceSuggestion) => {
    setResolving(true);
    const location = await fetchPlaceLocation(suggestion.placeId);
    setResolving(false);
    const picked: WorkLocation = {
      name: suggestion.mainText,
      address: location?.address || suggestion.secondaryText,
      latitude: location?.latitude,
      longitude: location?.longitude,
      placeId: suggestion.placeId,
    };
    onChange(picked);
    setQuery(picked.name);
    setSuggestions([]);
    setOpen(false);
  };

  const handleConfirmMapPick = () => {
    if (!pickedLocation) return;
    onChange({
      name: pickedLocation.name,
      address: pickedLocation.address,
      latitude: pickedLocation.latitude,
      longitude: pickedLocation.longitude,
    });
    setQuery(pickedLocation.name);
    setPickedLocation(null);
    setMode("search");
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setQuery("");
    setSuggestions([]);
  };

  const handleToggleMap = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
    setMode((m) => (m === "map" ? "search" : "map"));
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-stretch gap-1.5">
        <div
          className={`relative flex flex-1 min-w-0 items-center gap-2.5 rounded-lg border border-l-4 bg-white pl-3 pr-9 py-2 min-h-[38px] ${accentBorder} ${
            error ? "border-[#E24B4A]" : "border-[rgba(11,15,14,0.15)]"
          }`}
        >
          <MapPin size={16} className={`shrink-0 ${accentIcon}`} />
          <input
            type="text"
            value={query}
            onFocus={() => {
              setMode("search");
              setOpen(true);
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              setMode("search");
              setOpen(true);
            }}
            placeholder={placeholder}
            className="flex-1 min-w-0 border-0 p-0 text-[13px] font-medium text-[#2B2F2E] focus:outline-none focus:ring-0 bg-transparent truncate"
          />
          {resolving || loading ? (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#C97A3C]" />
          ) : query ? (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A9590] hover:text-[#E24B4A] transition-colors"
            >
              <X size={14} />
            </span>
          ) : (
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A9590] pointer-events-none" />
          )}
        </div>
        <button
          type="button"
          onClick={handleToggleMap}
          title={t("form.pickOnMap")}
          className={`shrink-0 w-9 flex items-center justify-center rounded-lg border transition-colors ${
            mode === "map" && open
              ? "bg-[#C97A3C] border-[#C97A3C] text-white"
              : "bg-white border-[rgba(11,15,14,0.15)] text-[#8A9590] hover:text-[#C97A3C]"
          }`}
        >
          <MapIcon size={15} />
        </button>
        {value?.latitude != null && value?.longitude != null && (
          <a
            href={`https://www.google.com/maps?q=${value.latitude},${value.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            title={t("form.openInMaps")}
            className="shrink-0 w-9 flex items-center justify-center rounded-lg border bg-white border-[rgba(11,15,14,0.15)] text-[#8A9590] hover:text-[#C97A3C] transition-colors"
          >
            <ExternalLink size={15} />
          </a>
        )}
      </div>

      {open && mode === "search" && query.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#ECEAE4] rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto">
          {suggestions.length > 0 ? (
            suggestions.map((s) => (
              <button
                key={s.placeId}
                type="button"
                onClick={() => handleSelectSuggestion(s)}
                className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-[#FAF9F6] transition-colors"
              >
                <MapPin size={14} className="mt-0.5 shrink-0 text-[#8A9590]" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-[#2B2F2E] truncate">{s.mainText}</div>
                  {s.secondaryText && (
                    <div className="text-[11px] text-[#8A9590] truncate">{s.secondaryText}</div>
                  )}
                </div>
              </button>
            ))
          ) : (
            <div className="px-3 py-3 text-[12px] text-[#8A9590] text-center">
              {loading ? t("form.placeholders.searchLocation") : t("form.noLocationResults")}
            </div>
          )}
        </div>
      )}

      {open && mode === "map" && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#ECEAE4] rounded-lg shadow-lg overflow-hidden">
          <div ref={mapContainerRef} className="h-56 w-full" />
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-[#ECEAE4]">
            <div className="min-w-0 flex-1 flex items-center gap-1.5 text-[12px] text-[#8A9590]">
              {mapLoading && <Loader2 size={13} className="animate-spin text-[#C97A3C] shrink-0" />}
              <span className="truncate">
                {pickedLocation ? pickedLocation.address || pickedLocation.name : t("form.mapPickHint")}
              </span>
            </div>
            <button
              type="button"
              disabled={!pickedLocation}
              onClick={handleConfirmMapPick}
              className="shrink-0 px-3 py-1.5 rounded-md text-[12px] font-medium bg-[#C97A3C] text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            >
              {t("form.useThisLocation")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
